import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Alert, Snackbar,
  Tooltip, IconButton, CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Print as PrintIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { academicAPI, teacherAPI, timetableAPI } from '../services/api';
import { errorText } from '../utils/format';
import { printPage, esc } from '../utils/print';

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const DAY_LABEL = { MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat' };
const SLOTS = [['09:00', '09:50'], ['09:50', '10:40'], ['10:50', '11:40'], ['11:40', '12:30'], ['13:30', '14:20'], ['14:20', '15:10'], ['15:20', '16:10'], ['16:10', '17:00']];
const PALETTE = ['#e0f2f1', '#e3f2fd', '#fff3e0', '#f3e5f5', '#fce4ec', '#e8f5e9', '#fffde7', '#ede7f6', '#e0f7fa', '#fbe9e7'];

function colorFor(name, map) {
  if (!map.has(name)) map.set(name, PALETTE[map.size % PALETTE.length]);
  return map.get(name);
}

function Grid7({ entries, periods, onCell, showBatch, readOnly }) {
  const colors = new Map();
  const byKey = new Map(entries.map((e) => [`${e.day}-${e.period}`, e]));
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="table" sx={{ width: '100%', minWidth: 820, borderCollapse: 'separate', borderSpacing: 6 }}>
        <thead>
          <tr>
            <Box component="th" sx={{ width: 96, p: 1, color: 'text.secondary', fontSize: 12, textAlign: 'left' }}>Period</Box>
            {DAYS.map((d) => <Box key={d} component="th" sx={{ p: 1, bgcolor: '#00796b', color: '#fff', borderRadius: 2, fontSize: 13 }}>{DAY_LABEL[d]}</Box>)}
          </tr>
        </thead>
        <tbody>
          {periods.map((p) => (
            <tr key={p}>
              <Box component="td" sx={{ p: 1, verticalAlign: 'middle' }}>
                <Typography variant="body2" fontWeight={800}>Period {p}</Typography>
                <Typography variant="caption" color="text.secondary">{SLOTS[p - 1]?.[0]} - {SLOTS[p - 1]?.[1]}</Typography>
              </Box>
              {DAYS.map((d) => {
                const e = byKey.get(`${d}-${p}`);
                return (
                  <Box key={d} component="td" onClick={() => !readOnly && onCell(d, p, e)}
                    sx={{ p: 1, height: 74, verticalAlign: 'top', borderRadius: 2, cursor: readOnly ? 'default' : 'pointer', bgcolor: e ? colorFor(e.subjectName, colors) : '#fafafa',
                      border: e ? '1px solid rgba(0,0,0,0.06)' : '1px dashed #cfd8dc', '&:hover': readOnly ? {} : { boxShadow: '0 2px 10px rgba(0,0,0,0.15)' } }}>
                    {e ? (
                      <>
                        <Typography variant="caption" fontWeight={800} sx={{ display: 'block', lineHeight: 1.2 }}>{e.subjectName}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{showBatch ? `${e.className} · ${e.sectionName}` : e.teacherName}</Typography>
                        {(e.roomNo) && <Typography variant="caption" sx={{ display: 'block', color: '#00796b' }}>{e.roomNo}</Typography>}
                      </>
                    ) : !readOnly ? <Box sx={{ color: '#b0bec5', display: 'flex', justifyContent: 'center', mt: 1.5 }}><AddIcon fontSize="small" /></Box> : null}
                  </Box>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

export default function TimetableManagement() {
  const [tab, setTab] = useState(0);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cell, setCell] = useState(null);
  const [form, setForm] = useState({ subjectId: '', teacherId: '', roomNo: '', startTime: '', endTime: '' });
  const [error, setError] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFrom, setCopyFrom] = useState('');
  const [allBatches, setAllBatches] = useState([]);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => setSnack({ open: true, message, severity }), []);

  useEffect(() => {
    Promise.all([academicAPI.getClasses(), academicAPI.getSubjects(), teacherAPI.getAll(), teacherAPI.getAssignments(), academicAPI.getSections()])
      .then(([c, s, t, a, sec]) => {
        setClasses(c.data); setSubjects(s.data); setTeachers(t.data); setAssignments(a.data); setAllBatches(sec.data);
        const first = c.data.find((x) => sec.data.some((b) => b.schoolClass?.id === x.id));
        if (first) setClassId(first.id);
        if (t.data[0]) setTeacherId(t.data[0].id);
      }).catch((e) => notify(errorText(e, 'Could not load the timetable data'), 'error'));
  }, [notify]);

  useEffect(() => {
    setSectionId('');
    if (!classId) { setSections([]); return; }
    academicAPI.getSectionsByClass(classId).then((r) => { setSections(r.data); if (r.data[0]) setSectionId(r.data[0].id); }).catch(() => setSections([]));
  }, [classId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 0 && classId && sectionId) setEntries((await timetableAPI.getByBatch(classId, sectionId)).data);
      else if (tab === 1 && teacherId) setEntries((await timetableAPI.getForTeacher(teacherId)).data);
      else setEntries([]);
    } catch (e) { notify(errorText(e, 'Could not load the timetable'), 'error'); }
    setLoading(false);
  }, [tab, classId, sectionId, teacherId, notify]);
  useEffect(() => { load(); }, [load]);

  const periods = Array.from({ length: Math.max(6, ...entries.map((e) => e.period), 0) }, (_, i) => i + 1);

  const openCell = (day, period, entry) => {
    setError('');
    setCell({ day, period, entry });
    setForm(entry ? { subjectId: entry.subjectId, teacherId: entry.teacherId, roomNo: entry.roomNo || '', startTime: entry.startTime || '', endTime: entry.endTime || '' }
      : { subjectId: '', teacherId: '', roomNo: '', startTime: SLOTS[period - 1]?.[0] || '', endTime: SLOTS[period - 1]?.[1] || '' });
  };

  const pickSubject = (subjectId) => {
    const match = assignments.find((a) => a.schoolClass?.id === classId && a.subject?.id === subjectId);
    setForm((f) => ({ ...f, subjectId, teacherId: f.teacherId || match?.teacher?.id || '' }));
  };

  const save = async () => {
    setError('');
    try {
      await timetableAPI.save({ classId, sectionId, day: cell.day, period: cell.period, subjectId: form.subjectId, teacherId: form.teacherId, roomNo: form.roomNo, startTime: form.startTime, endTime: form.endTime });
      setCell(null);
      notify('Timetable updated');
      load();
    } catch (e) { setError(errorText(e, 'Could not save this period')); }
  };

  const remove = async () => {
    try { await timetableAPI.remove(cell.entry.id); setCell(null); notify('Period removed'); load(); }
    catch (e) { setError(errorText(e, 'Could not remove this period')); }
  };

  const copy = async () => {
    try { const r = await timetableAPI.copy({ fromSectionId: copyFrom, toSectionId: sectionId }); notify(r.data.message); setCopyOpen(false); load(); }
    catch (e) { notify(errorText(e, 'Could not copy'), 'error'); }
  };

  const batchName = sections.find((s) => s.id === sectionId)?.name;
  const className = classes.find((c) => c.id === classId)?.name;

  const print = () => {
    const title = tab === 0 ? `${className} - ${batchName}` : teachers.find((t) => t.id === teacherId) ? `${teachers.find((t) => t.id === teacherId).firstName} ${teachers.find((t) => t.id === teacherId).lastName}` : '';
    const byKey = new Map(entries.map((e) => [`${e.day}-${e.period}`, e]));
    const body = `<table><thead><tr><th>Period</th>${DAYS.map((d) => `<th class="c">${DAY_LABEL[d]}</th>`).join('')}</tr></thead><tbody>
      ${periods.map((p) => `<tr><td><b>Period ${p}</b><br/>${SLOTS[p - 1]?.[0] || ''} - ${SLOTS[p - 1]?.[1] || ''}</td>${DAYS.map((d) => {
        const e = byKey.get(`${d}-${p}`);
        return `<td class="c">${e ? `<b>${esc(e.subjectName)}</b><br/>${esc(tab === 0 ? e.teacherName : `${e.className} · ${e.sectionName}`)}${e.roomNo ? '<br/>' + esc(e.roomNo) : ''}` : ''}</td>`;
      }).join('')}</tr>`).join('')}</tbody></table>`;
    printPage(tab === 0 ? 'Class Timetable' : 'Faculty Timetable', title, body);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4">Timetable</Typography>
          <Typography variant="body2" color="text.secondary">Build the weekly timetable for each batch. Students see it in the Student app and faculty see their own schedule in the Faculty app.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={print} disabled={!entries.length}>Print</Button>
      </Box>

      <Paper sx={{ borderRadius: 3, mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 2, borderBottom: '1px solid #eee' }}>
          <Tab label="Class timetable" />
          <Tab label="Faculty timetable" />
        </Tabs>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {tab === 0 ? (
            <>
              <TextField select size="small" label="Program / year" value={classId} onChange={(e) => setClassId(e.target.value)} sx={{ minWidth: 260 }}>
                {classes.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Batch" value={sectionId} onChange={(e) => setSectionId(e.target.value)} sx={{ minWidth: 150 }}>
                {sections.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </TextField>
              <Button variant="outlined" size="small" startIcon={<CopyIcon />} onClick={() => { setCopyFrom(''); setCopyOpen(true); }} disabled={!sectionId}>Copy from another batch</Button>
            </>
          ) : (
            <TextField select size="small" label="Faculty" value={teacherId} onChange={(e) => setTeacherId(e.target.value)} sx={{ minWidth: 280 }}>
              {teachers.map((t) => <MenuItem key={t.id} value={t.id}>{t.firstName} {t.lastName}{t.designation ? ` (${t.designation})` : ''}</MenuItem>)}
            </TextField>
          )}
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 3, p: 2 }}>
        {tab === 0 && !sectionId ? <Alert severity="info">Choose a program and batch. Programs without batches must have a batch created first under Programs &amp; Batches.</Alert>
          : loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            : <Grid7 entries={entries} periods={periods.length ? periods : [1, 2, 3, 4, 5, 6]} onCell={openCell} showBatch={tab === 1} readOnly={tab === 1} />}
        {tab === 0 && sectionId && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Click a slot to add or change a period. A faculty member cannot be booked in two batches at the same time.</Typography>}
      </Paper>

      <Dialog open={!!cell} onClose={() => setCell(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>
          {cell && `${cell.day.charAt(0)}${cell.day.slice(1).toLowerCase()} · Period ${cell.period}`}
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>{className} · {batchName}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Subject" value={form.subjectId} onChange={(e) => pickSubject(e.target.value)}>
                {subjects.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Faculty" value={form.teacherId} onChange={(e) => setForm({ ...form, teacherId: e.target.value })}
                helperText="Picked automatically from the faculty assigned to this subject, if any">
                {teachers.map((t) => <MenuItem key={t.id} value={t.id}>{t.firstName} {t.lastName}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="time" label="Starts" InputLabelProps={{ shrink: true }} value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="time" label="Ends" InputLabelProps={{ shrink: true }} value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Room / place (optional)" value={form.roomNo} onChange={(e) => setForm({ ...form, roomNo: e.target.value })} placeholder="e.g. Classroom 101, Skill lab" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, justifyContent: 'space-between' }}>
          <Box>{cell?.entry && <Tooltip title="Remove this period"><IconButton color="error" onClick={remove}><DeleteIcon /></IconButton></Tooltip>}</Box>
          <Box><Button onClick={() => setCell(null)}>Cancel</Button><Button variant="contained" onClick={save} disabled={!form.subjectId || !form.teacherId}>Save</Button></Box>
        </DialogActions>
      </Dialog>

      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Copy a timetable into {className} · {batchName}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>Periods that already exist here will be replaced. Periods where the faculty member is busy in another batch are skipped.</Alert>
          <TextField select fullWidth size="small" label="Copy from" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
            {allBatches.filter((b) => b.id !== sectionId).map((b) => <MenuItem key={b.id} value={b.id}>{b.schoolClass?.name} · {b.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setCopyOpen(false)}>Cancel</Button><Button variant="contained" disabled={!copyFrom} onClick={copy}>Copy</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
